import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { LogOut } from "lucide-react";

import AdminEpisodes from "@/components/admin/AdminEpisodes";
import AdminSponsors from "@/components/admin/AdminSponsors";
import AdminShows from "@/components/admin/AdminShows";
import AdminHomepage from "@/components/admin/AdminHomepage";
import AdminSettings from "@/components/admin/AdminSettings";
import AdminAnalytics from "@/components/admin/AdminAnalytics";

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();

  const [stats, setStats] = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const [shows, setShows] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [subs, setSubs] = useState([]);
  const [homepage, setHomepage] = useState(null);

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) nav("/admin/login");
  }, [user, loading, nav]);

  const refresh = useCallback(async () => {
    try {
      const [stat, ep, sh, sp, ct, iq, ns, hp] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/episodes", { params: { limit: 500 } }),
        api.get("/shows"),
        api.get("/sponsors", { params: {} }),
        api.get("/admin/submissions/contacts"),
        api.get("/admin/submissions/sponsor-inquiries"),
        api.get("/admin/submissions/newsletter"),
        api.get("/admin/homepage"),
      ]);
      setStats(stat.data);
      setEpisodes(ep.data);
      setShows(sh.data);
      setSponsors(sp.data);
      setContacts(ct.data);
      setInquiries(iq.data);
      setSubs(ns.data);
      setHomepage(hp.data);
    } catch (e) {
      console.warn("admin dashboard refresh failed", e);
    }
  }, []);

  useEffect(() => {
    if (user && user.role === "admin") refresh();
  }, [user, refresh]);

  if (loading || !user) return <div className="px-6 py-24 text-neutral-400">Loading…</div>;

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12" data-testid="admin-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-gold">Network CMS</p>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight">Mission control.</h1>
        </div>
        <button onClick={async () => { await logout(); nav("/"); }} data-testid="admin-logout-btn" className="inline-flex items-center gap-2 border border-white/20 px-5 py-3 text-xs font-bold uppercase tracking-widest hover:bg-white/5">
          <LogOut size={14} /> Logout
        </button>
      </div>

      {stats && (
        <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-3" data-testid="admin-stats">
          {[
            ["Shows", stats.shows], ["Episodes", stats.episodes], ["Sponsors", stats.sponsors],
            ["Contacts", stats.contacts], ["Inquiries", stats.sponsor_inquiries],
            ["Subscribers", stats.newsletter_subscribers], ["Page views", stats.total_pageviews],
          ].map(([k, v]) => (
            <div key={k} className="bg-[#0F0F0F] border border-white/5 p-5">
              <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-neutral-500">{k}</p>
              <p className="mt-2 font-display text-3xl font-black text-gold">{v}</p>
            </div>
          ))}
        </div>
      )}

      <Tabs defaultValue="homepage" className="mt-12">
        <TabsList className="bg-black border border-white/10 rounded-none p-1 flex-wrap h-auto">
          {[
            ["homepage", "Homepage"], ["shows", "Shows"], ["episodes", "Episodes"],
            ["sponsors", "Sponsors"], ["analytics", "Analytics"], ["settings", "Settings"],
            ["contacts", "Contacts"], ["inquiries", "Inquiries"], ["newsletter", "Newsletter"],
          ].map(([id, label]) => (
            <TabsTrigger key={id} value={id} className="rounded-none px-4 py-2 data-[state=active]:bg-svn-red data-[state=active]:text-white text-xs font-bold uppercase tracking-widest" data-testid={`admin-tab-${id}`}>
              {label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="homepage" className="mt-6">
          {homepage ? (
            <AdminHomepage initial={homepage} shows={shows} episodes={episodes} sponsors={sponsors} refresh={refresh} />
          ) : <p className="text-neutral-400">Loading…</p>}
        </TabsContent>

        <TabsContent value="shows" className="mt-6">
          <AdminShows shows={shows} refresh={refresh} />
        </TabsContent>

        <TabsContent value="episodes" className="mt-6">
          <AdminEpisodes episodes={episodes} shows={shows} refresh={refresh} />
        </TabsContent>

        <TabsContent value="sponsors" className="mt-6">
          <AdminSponsors sponsors={sponsors} refresh={refresh} />
        </TabsContent>

        <TabsContent value="analytics" className="mt-6">
          <AdminAnalytics />
        </TabsContent>

        <TabsContent value="settings" className="mt-6">
          <AdminSettings refresh={refresh} />
        </TabsContent>

        <TabsContent value="contacts" className="mt-6">
          <SimpleTable rows={contacts} columns={["created_at", "type", "name", "email", "subject", "message"]} testId="contacts-table" />
        </TabsContent>
        <TabsContent value="inquiries" className="mt-6">
          <SimpleTable rows={inquiries} columns={["created_at", "name", "email", "company", "package", "budget", "message"]} testId="inquiries-table" />
        </TabsContent>
        <TabsContent value="newsletter" className="mt-6">
          <SimpleTable rows={subs} columns={["created_at", "email"]} testId="newsletter-table" />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function SimpleTable({ rows, columns, testId }) {
  return (
    <div className="bg-[#0F0F0F] border border-white/10 overflow-x-auto" data-testid={testId}>
      <table className="w-full text-sm">
        <thead className="bg-black/60">
          <tr>
            {columns.map((c) => <th key={c} className="text-left text-[10px] font-bold uppercase tracking-[0.2em] text-neutral-400 px-4 py-3">{c.replace(/_/g, " ")}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={columns.length} className="px-4 py-8 text-center text-neutral-500">No entries yet.</td></tr>
          ) : (
            rows.map((r) => (
              <tr key={r.id} className="border-t border-white/5">
                {columns.map((c) => (
                  <td key={c} className="px-4 py-3 text-neutral-300 max-w-[260px] truncate" title={String(r[c] ?? "")}>{String(r[c] ?? "")}</td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
